import { useMemo } from 'react';
import { verifiers } from '@yola/subscription-manager-js';
import configStorage from '../../configuration/config-storage';
import getPriceData from '../helpers/get-price-data';
import { PER_YEAR, API_TERM_VALUES } from '../../common/constants/periods';
import { t } from '../../localization';
import getPlanLabel from '../helpers/get-plan-label';
import { getExtraPriceInfoTooltip, getPlanDescription } from '../helpers/get-captions';

const usePlansData = ({
  currentPlan,
  currentTerm,
  prices,
  currency,
  minRequiredPlan,
  disabledPlans,
  paymentPeriod,
  discountValue,
  availablePlansData,
  areEcommercePlans,
  onPlanSelect,
}) => {
  const { locale = 'en' } = configStorage.get();

  return useMemo(() => {
    const { planIds: supportedPlanList } = availablePlansData;
    const preparedPlans = supportedPlanList
      .filter((plan) => !disabledPlans.includes(plan))
      .map((plan) => ({
        name: availablePlansData.map[plan].name,
        mostPopular: Boolean(availablePlansData.map[plan].meta.mostPopular),
        type: plan,
        id: plan,
        price: getPriceData({ prices, plan, currency, locale }),
        current: plan === currentPlan,
        available: verifiers.isPlanAvailable({
          plan,
          currentPlan,
          minRequiredPlan,
          term: API_TERM_VALUES[paymentPeriod],
          currentTerm,
        }),
      }));

    const mostPopularPlanIndex = preparedPlans.findIndex((plan) => plan.mostPopular);
    const mostValuablePlanIndex =
      mostPopularPlanIndex < 0
        ? mostPopularPlanIndex
        : preparedPlans.findIndex((plan, index) => plan.available && index >= mostPopularPlanIndex);

    const isFreePackageType = areEcommercePlans
      ? verifiers.isFreeEcommercePlanType
      : verifiers.isFreeHostingPackageType;

    return preparedPlans.map((plan, index) => {
      const { type, available, price, mostPopular, name, id } = plan;
      const isMostValuable = mostValuablePlanIndex === index;
      const { extraPrice } = price;
      const perMonth = price.perMonth?.split(/[.,]+/) || [];
      const perYear = {
        month: price.perYear.month?.split(/[.,]+/) || [],
        total: price.perYear.total?.split(/[.,]+/) || [],
      };

      return {
        captions: {
          pricing: {
            first: paymentPeriod === PER_YEAR ? perYear.month[0] : perMonth[0],
            second: paymentPeriod === PER_YEAR ? perYear.month[1] : perMonth[1],
            period: t('/ Month'),
          },
          ...(discountValue
            ? {
                extraPrice: {
                  infoText: getExtraPriceInfoTooltip(discountValue),
                  price: paymentPeriod === PER_YEAR ? extraPrice.perYear : extraPrice.perMonth,
                },
              }
            : {}),
          name,
          description: getPlanDescription(type),
          cta: t('Select'),
          label: getPlanLabel(type, currentPlan, mostPopular, isMostValuable),
          ...(paymentPeriod === PER_YEAR && {
            priceDescription: !isFreePackageType(id) ? t('billed annually') : '',
          }),
        },
        disabled: !available,
        highlighted: isMostValuable,
        type,
        id,
        onPlanSelect,
      };
    });
  }, [
    currentPlan,
    prices,
    currency,
    locale,
    minRequiredPlan,
    onPlanSelect,
    disabledPlans,
    paymentPeriod,
    discountValue,
    availablePlansData,
    currentTerm,
    areEcommercePlans,
  ]);
};

export default usePlansData;
